import { useEffect } from 'react';
import {Button, Checkbox, DatePicker, Input, Select, Tag} from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import moment, {Moment} from 'moment';

import { usePopupContext } from '../../Helpers/PopupProvider';

import { FormPopupField, FormPopupProps } from "./FormPopup.model";

import styles from "./Popup.module.scss";

const FormPupup = ({ actions, editData, editId, fields, onCancel, onSubmitForm, title }: FormPopupProps) => {
  const { TextArea } = Input;
  const { Option } = Select;

  const intl = useIntl();
  const { onPopupClose, onPopupOpen } = usePopupContext();

  useEffect(() => {
    onPopupOpen();
  }, [])

  const mapFields = (fields: FormPopupField[]) => {
    return fields.reduce((acc: any, curr) => {
      if (curr.type === 'selectMultiple') {
        acc[curr.id] = [];
      } else {
        acc[curr.id] = undefined;
      };

      return acc;
    }, {});
  }

  const formatEditData = (data: any) => {
    Object.keys(data).forEach(key => {
      if (fields.find(field => field.id === key && field.type === 'date') && data[key])
        data[key] = moment(data[key], 'YYYY-MM-DD');
      if (fields.find(field => field.id === key && field.type === 'year') && data[key])
        data[key] = moment(data[key], 'YYYY');
    });
    return data;
  }

  const disabledDate = (current: Moment, fieldName: string) => {
    if(fieldName === 'endDate') 
      return current <= getValues('startDate')
    if(getValues('endDate'))
      return getValues('endDate') < current;
    return false;
  }

  const defaultValues: any = editData ? formatEditData({...editData}) : mapFields(fields);

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm({ defaultValues });

  const renderInput = (formField: any) => {
    return (
      <div className={`${styles.fieldWrapper}  ${styles[formField.layout]}`}
        key={formField.id}>
        <label className={styles.floatingLabel}>
          {formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
        </label>
        <Controller
          name={formField.id}
          control={control}
          rules={formField.rules}
          render={({ field }) => (
            <Input
              {...field}
              className={errors[formField.id] && 'error'}
              placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
            />
          )}
        />
      </div>
    );
  };

  const renderTextArea = (formField: any) => {
    return (
      <div className={`${styles.fieldWrapper}  ${styles[formField.layout]}`}
        key={formField.id}>
        <label className={styles.floatingLabel}>
          {formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
        </label>
        <Controller
          name={formField.id}
          control={control}
          rules={formField.rules}
          render={({ field }) => (
            <TextArea
              {...field}
              className={errors[formField.id] && 'error'}
              placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
              rows={6}
            />
          )}
        />
      </div>
    );
  };

  const renderSelect = (formField: FormPopupField) => {
    return <div className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
      key={formField.id}>
      <label className={styles.floatingLabel}>
        {formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
      </label>
      <Controller
        name={formField.id}
        control={control}
        rules={formField.rules}
        render={({ field }) => (
          <Select
            {...field}
            className={errors[formField.id] && 'error'}
            filterOption={formField.options?.filterOption ? formField.options?.filterOption : (input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            optionFilterProp="children"
            placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
            style={{ width: '100%' }}
            showSearch
          >
            {formField.options && formField.options.optionList.map((option: any, index: number) => (
              <Option
                key={`option${formField.id}${index}`}
                value={option.value}
              >
                {formField.options?.type === 'codeBased' ? option.label : intl.formatMessage({ id: `${formField.id}.${option.label}`})}
              </Option>
            ))}
          </Select>
        )}
      />
    </div>
  }

  const renderSelectMultiple = (formField: FormPopupField) => {
    return <div className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
      key={formField.id}>
      <label className={styles.floatingLabel}>
        {formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
      </label>
      <Controller
        name={formField.id}
        control={control}
        rules={formField.rules}
        render={({ field }) => (
          <Select
            {...field}
            className={errors[formField.id] && 'error'}
            mode="multiple"
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
            style={{ width: '100%' }}
            tagRender={({ label }) => (
              <Tag
                style={{
                  border: 'none',
                  textTransform: 'uppercase'
                }}
              >
                {label}
              </Tag>
            )}
          >
						{
              formField.options && formField.options.optionList.map((option: any, index: number) =>
                <Option value={option.value}  key={`option${formField.id}${index}`}>
                  {formField.options?.type === 'codeBased' ? option.label : intl.formatMessage({ id: `${formField.id}.${option.label}`})}
                </Option>
              )
            }
          </Select>
        )}
      />
    </div>
  };

  const renderTags = (formField: FormPopupField) => {
    return <div className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
      key={formField.id}>
      <label className={styles.floatingLabel}>
        {formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
      </label>
      <Controller
        name={formField.id}
        control={control}
        rules={formField.rules}
        render={({ field }) => (
          <Select
            {...field}
            className={errors[formField.id] && 'error'}
            mode="tags"
            placeholder={formField.placeholder || intl.formatMessage({ id: `formPlaceholders.${formField.id}` })}
            style={{ width: '100%' }}
            tagRender={({ label }) => (
              <Tag
                style={{
                  border: 'none',
                  textTransform: 'uppercase'
                }}
              >
                {label}
              </Tag>
            )}
          >
						{
              formField.options && formField.options.optionList.map((option: any, index: number) =>
                <Option value={option.value}  key={`option${formField.id}${index}`}>
                  {formField.options?.type === 'codeBased' ? option.label : intl.formatMessage({ id: `${formField.id}.${option.label}`})}
                </Option>
              )
            }
          </Select>
        )}
      />
    </div>
  };

  const renderDatePicker = (formField: FormPopupField) => {
    return (
      <div
        className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
        key={formField.id}
      >
        <label className={styles.floatingLabel}>
          {formField.label || <FormattedMessage id={`userLabels.${formField.id}`} />}
        </label>
        <Controller
          name={formField.id}
          control={control}
          rules={formField.rules}
          render={({field}) => (
            <DatePicker
              {...field}
              className={errors[formField.id] && 'error'}
              format={formField.type === 'year' ? 'YYYY' : 'DD MMM YYYY'}
              suffixIcon={false}
              style={{ padding: 0, width: '100%' }}
              picker={formField.type === 'year' ? 'year' : 'date'}
              disabledDate={(current) => disabledDate(current, field.name)}
            />
          )}
        />
      </div>
    );
  };

	const renderCheckbox = (formField: FormPopupField) => {
		return (
			<div
				className={`${styles.fieldWrapper} ${styles[formField.layout]}`}
				key={formField.id}
			>
				<Controller
					name={formField.id}
					control={control}
					rules={formField.rules}
					render={({ field }) => (
						<Checkbox {...field} className={styles.checkbox}>
							{formField.label || (
								<FormattedMessage id={`formLabels.${formField.id}`} />
							)}
						</Checkbox>
					)}
				/>
			</div>
		);
	};

  const renderField = (field: any, type: string) => {
    switch (type) {
      case "text":
        return renderInput(field);
      case "textArea":
        return renderTextArea(field);
      case "select":
        return renderSelect(field);
      case "selectMultiple":
        return renderSelectMultiple(field);
      case "tags":
          return renderTags(field);
      case "date":
        return renderDatePicker(field);
      case "year":
        return renderDatePicker(field);
			case "checkbox":
				return renderCheckbox(field);
			default: return renderInput(field);
    }
  };

  const renderForm = () => {
    return <div className={styles.fields}>
      {fields.map((field: any) => renderField(field, field.type))}
    </div>
  }

  const onSubmit = (formData: any) => {
    onPopupClose();
    editData ? onSubmitForm(formData, editId) : onSubmitForm(formData);
    onCancel();
  }

  const handleAction = (action: any) => {
    onPopupClose(action);
  }

  return (
		<div className={styles.popupContainer}>
			<div  onClick={onCancel}  className={styles.backdrop} />
      <div className={`${styles.popupWrapper} ${styles.topBorder}`}>
        <div className={styles.header}>
          <h1>
            {title.action && <b><FormattedMessage id={`actions.${title.action}`} />:&nbsp;</b>}
            {title.target && <FormattedMessage id={`popupTitles.${title.target}`} />}
          </h1>
        </div>
        <div className={styles.form}>
          {renderForm()}
        </div>
        <div className={styles.actions}>
          {actions.map((button, index) =>
            <Button size="large" onClick={button.isSubmitButton ? handleSubmit(onSubmit) : handleAction.bind(null, button.onClick)} type={button.type} key={`${button.title}${index}`}>
              <FormattedMessage id={`buttons.${button.title}`} />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FormPupup;